import {
  Button,
  Col,
  FieldLabel,
  Form,
  NumericFormatField,
  RemovableCard,
  Row,
  TextInputField,
} from '@/components';
import { useProjectFormContext } from '@/features/project/_hooks/form-context';
import { PlusOutlined } from '@ant-design/icons';
import { type FC, useEffect } from 'react';
import {
  type UseFieldArrayRemove,
  useFieldArray,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export const JumlahTargetPenerimaManfaatArrayField: FC = () => {
  const { control } = useProjectFormContext();

  const { t } = useTranslation();

  const {
    fields: jumlahTargetPenerimaManfaatFields,
    append: appendjumlahTargetPenerimaManfaat,
    remove: removejumlahTargetPenerimaManfaat,
  } = useFieldArray({
    control,
    name: 'data.indonesiaData.jumlahTargetPenerimaManfaat',
  });

  return (
    <Form.Item
      label={<FieldLabel text={t('jumlah-target-penerima-manfaat')} />}
    >
      <Row gutter={[24, 8]}>
        {jumlahTargetPenerimaManfaatFields.map((item, idx) => {
          return (
            <Col key={item.id} span={24}>
              <RelatedProjectField
                idx={idx}
                removeRelatedProject={removejumlahTargetPenerimaManfaat}
              />
            </Col>
          );
        })}
        <Col span={24}>
          <Button
            type="dashed"
            onClick={() => {
              appendjumlahTargetPenerimaManfaat({
                elementValue: null,
                elementUnit: null,
                element2Value: null,
                element2Unit: null,
                ratio: null,
              });
            }}
            block
            icon={<PlusOutlined />}
          >
            Penambahan jumlah target penerima manfaat
          </Button>
        </Col>
      </Row>
    </Form.Item>
  );
};

const RelatedProjectField = ({
  idx,
  removeRelatedProject,
}: {
  idx: number;
  removeRelatedProject: UseFieldArrayRemove;
}) => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
    setValue,
  } = useProjectFormContext();

  const values = useWatch({
    control,
    name: `data.indonesiaData.jumlahTargetPenerimaManfaat.${idx}`,
  });

  useEffect(() => {
    if (values?.elementValue && values?.element2Value) {
      setValue(
        `data.indonesiaData.jumlahTargetPenerimaManfaat.${idx}.ratio`,
        (values.elementValue / values.element2Value) * 100,
      );
    } else {
      setValue(
        `data.indonesiaData.jumlahTargetPenerimaManfaat.${idx}.ratio`,
        null,
      );
    }
  }, [idx, values?.elementValue, values?.element2Value, setValue]);

  return (
    <RemovableCard
      onRemove={() => {
        removeRelatedProject(idx);
      }}
    >
      <Row gutter={[24, 0]}>
        <Col span={12}>
          <NumericFormatField
            key={`data.indonesiaData.jumlahTargetPenerimaManfaat.${idx}.elementValue`}
            name={`data.indonesiaData.jumlahTargetPenerimaManfaat.${idx}.elementValue`}
            label={t('value')}
            control={control}
            errors={errors}
            allowNegative={false}
          />
        </Col>
        <Col span={12}>
          <TextInputField
            key={`data.indonesiaData.jumlahTargetPenerimaManfaat.${idx}.elementUnit`}
            name={`data.indonesiaData.jumlahTargetPenerimaManfaat.${idx}.elementUnit`}
            label={t('unit')}
            control={control}
            errors={errors}
          />
        </Col>
        <Col span={12}>
          <NumericFormatField
            key={`data.indonesiaData.jumlahTargetPenerimaManfaat.${idx}.element2Value`}
            name={`data.indonesiaData.jumlahTargetPenerimaManfaat.${idx}.element2Value`}
            label={t('value')}
            control={control}
            errors={errors}
            allowNegative={false}
          />
        </Col>
        <Col span={12}>
          <TextInputField
            key={`data.indonesiaData.jumlahTargetPenerimaManfaat.${idx}.element2Unit`}
            name={`data.indonesiaData.jumlahTargetPenerimaManfaat.${idx}.element2Unit`}
            label={t('unit')}
            control={control}
            errors={errors}
          />
        </Col>
        <Col span={12}>
          <NumericFormatField
            key={`data.indonesiaData.jumlahTargetPenerimaManfaat.${idx}.ratio`}
            name={`data.indonesiaData.jumlahTargetPenerimaManfaat.${idx}.ratio`}
            label={t('ratio')}
            control={control}
            errors={errors}
            disabled
            addonAfter={t('percentage-unit')}
          />
        </Col>
      </Row>
    </RemovableCard>
  );
};
